import {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, Button, type PaperProps, Skeleton, Stack, TextField, Typography} from "@mui/material";
import {RichTreeView} from "@mui/x-tree-view";
import {treeItemClasses} from "@mui/x-tree-view/TreeItem";
import InfiniteScroll from "react-infinite-scroll-component";
import {useStores} from "../../../../../stores";
import {useInsightSupplierListQuery} from "../../../hooks/useInsightSupplierListQuery";
import {
    buildSupplierTree,
    collectExpandableIds,
    collectSelectionsFromIds,
    collectVisibleSelectableItemIds,
    findItemIdsByNamesInOrder,
    findTreeItemById,
    getSelectableName,
    type SupplierPickerSelection,
    type SupplierTreeItem as SupplierTreeItemData,
} from "./SupplierTree";

export type SupplierMenuProps = {
    /** Called when selection changes; order follows tree item order, not click order. */
    onSupplierSelect?: (selections: SupplierPickerSelection[]) => void;
    /** Pre-check items that match these names when the tree is ready. */
    initialSupplierNames?: readonly string[];
} & Pick<PaperProps, "sx">;

const SEARCH_DEBOUNCE_MS = 300;
const SUPPLIER_MENU_SCROLL_ID = "supplier-menu-scroll";

export const SupplierMenu = forwardRef<HTMLDivElement, SupplierMenuProps>(
    function SupplierMenu({onSupplierSelect, initialSupplierNames, sx}, ref) {
        const {bagStore} = useStores();
        const bagId = bagStore.bagId ?? undefined;

        const [searchInput, setSearchInput] = useState("");
        const [debouncedSearch, setDebouncedSearch] = useState("");
        /** Source of truth: selected tree item ids; kept when search changes so picks survive paging/filter. */
        const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
        const selectedNameByIdRef = useRef<Map<string, string>>(new Map());

        useEffect(() => {
            const t = window.setTimeout(() => {
                setDebouncedSearch(searchInput.trim());
            }, SEARCH_DEBOUNCE_MS);
            return () => window.clearTimeout(t);
        }, [searchInput]);

        const {
            data,
            isPending,
            isError,
            error,
            fetchNextPage,
            hasNextPage,
        } = useInsightSupplierListQuery(bagId, debouncedSearch);

        const rows = useMemo(
            () => data?.pages.flatMap((page) => page.results) ?? [],
            [data?.pages],
        );

        const items: SupplierTreeItemData[] = useMemo(
            () => buildSupplierTree(rows),
            [rows],
        );
        const defaultExpandedItems = useMemo(
            () => (debouncedSearch ? collectExpandableIds(items) : []),
            [items, debouncedSearch],
        );

        const treeResetKey = `${bagId ?? "none"}-${debouncedSearch}`;
        const initialSelectionAppliedRef = useRef(false);

        useEffect(() => {
            initialSelectionAppliedRef.current = false;
        }, [initialSupplierNames]);

        useEffect(() => {
            if (
                initialSupplierNames === undefined
                || initialSelectionAppliedRef.current
                || items.length === 0
            ) {
                return undefined;
            }
            initialSelectionAppliedRef.current = true;
            const ids = initialSupplierNames.length > 0
                ? findItemIdsByNamesInOrder(items, initialSupplierNames)
                : [];
            const frameId = window.requestAnimationFrame(() => {
                for (const id of ids) {
                    const item = findTreeItemById(items, id);
                    const name = item ? getSelectableName(item) : undefined;
                    if (name) {
                        selectedNameByIdRef.current.set(id, name);
                    }
                }
                setSelectedItemIds(ids);
            });
            return () => window.cancelAnimationFrame(frameId);
        }, [initialSupplierNames, items]);

        const visibleSelectedIds = useMemo(
            () => selectedItemIds.filter((id) => Boolean(findTreeItemById(items, id))),
            [items, selectedItemIds],
        );

        const syncVisibleSelection = useCallback(
            (nextVisibleIds: readonly string[]) => {
                const visibleIdSet = new Set(collectVisibleSelectableItemIds(items));
                for (const id of nextVisibleIds) {
                    const treeItem = findTreeItemById(items, id);
                    const name = treeItem ? getSelectableName(treeItem) : undefined;
                    if (name) {
                        selectedNameByIdRef.current.set(id, name);
                    }
                }
                setSelectedItemIds((prev) => {
                    const offView = prev.filter((id) => !visibleIdSet.has(id));
                    const merged: string[] = [...offView];
                    for (const id of nextVisibleIds) {
                        if (!merged.includes(id)) {
                            merged.push(id);
                        }
                    }
                    return merged;
                });
            },
            [items],
        );

        return (
            <Box
                ref={ref}
                sx={{
                    maxHeight: "40vh",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    p: 1,
                    ...sx,
                }}
            >
                <TextField
                    placeholder="Search suppliers…"
                    variant="filled"
                    size="small"
                    fullWidth
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    autoFocus
                    sx={{
                        "& .MuiInputBase-root:before": {
                            border: "none",
                        },
                        "& .MuiInputBase-root:after": {
                            border: "none",
                        },
                        "& .MuiInputBase-root:hover:before": {
                            border: "none",
                        },
                        "& .MuiInputBase-root:hover:after": {
                            border: "none",
                        },
                        "& .MuiInputBase-sizeSmall": {
                            borderRadius: 1,
                        },
                        "& .MuiFilledInput-input": {
                            padding: "8px 12px",
                        },
                    }}
                />
                <Typography variant="caption" color="text.secondary" sx={{pt: "4px"}}>
                    From supplier normalization
                </Typography>
                {(bagId && isPending) ?
                    <Stack gap={1} direction="row" justifyContent="space-between">
                        <Skeleton width={60} height={20}/>
                        <Skeleton width={64} height={20}/>
                    </Stack> :
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                    >
                        <Typography
                            variant="caption"
                            color="color.secondary.light"
                        >{`${selectedItemIds.length} selected`}</Typography>
                        <Button
                            onClick={() => {
                                onSupplierSelect?.(collectSelectionsFromIds(
                                    selectedItemIds,
                                    items,
                                    selectedNameByIdRef.current,
                                ));
                            }}
                            variant="text"
                            color="primary"
                            size="small"
                            sx={{textTransform: "none", color: "#1976D2"}}
                            disabled={selectedItemIds.length === 0}
                        >
                            Insert
                        </Button>
                    </Stack>

                }
                <Box
                    id={SUPPLIER_MENU_SCROLL_ID}
                    sx={{
                        flex: 1,
                        minHeight: 120,
                        maxHeight: 300,
                        overflow: "auto",
                    }}
                >
                    {!bagId && (
                        <Typography variant="body2" color="text.secondary" sx={{p: 1}}>
                            No databag selected.
                        </Typography>
                    )}
                    {bagId && isPending && (
                        <Stack gap={1}>
                            <Skeleton height={20}/>
                            <Skeleton height={20}/>
                            <Skeleton height={20}/>
                            <Skeleton height={20}/>
                            <Skeleton height={20}/>
                            <Skeleton height={20}/>
                        </Stack>
                    )}
                    {bagId && isError && (
                        <Typography variant="body2" color="error" sx={{p: 1}}>
                            {error instanceof Error
                                ? error.message
                                : "Failed to load suppliers."}
                        </Typography>
                    )}
                    {bagId && !isPending && !isError && items.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{p: 1}}>
                            No suppliers match your search.
                        </Typography>
                    )}
                    {bagId && !isPending && !isError && items.length > 0 && (
                        <InfiniteScroll
                            scrollableTarget={SUPPLIER_MENU_SCROLL_ID}
                            dataLength={rows.length}
                            next={() => {
                                void fetchNextPage();
                            }}
                            hasMore={Boolean(hasNextPage)}
                            loader={
                                <Stack gap={1}>
                                    <Skeleton height={20}/>
                                    <Skeleton height={20}/>
                                    <Skeleton height={20}/>
                                </Stack>
                            }
                            scrollThreshold="80px"
                            style={{overflow: "visible"}}
                        >
                            <RichTreeView<SupplierTreeItemData, true>
                                key={treeResetKey}
                                items={items}
                                multiSelect
                                checkboxSelection
                                selectionPropagation={{descendants: false, parents: false}}
                                expansionTrigger="iconContainer"
                                defaultExpandedItems={defaultExpandedItems}
                                defaultSelectedItems={[]}
                                selectedItems={visibleSelectedIds}
                                onItemClick={(event, itemId) => {
                                    const target = event.target as HTMLElement;
                                    if (
                                        target.closest(".MuiCheckbox-root")
                                        || target.closest(`.${treeItemClasses.iconContainer}`)
                                    ) {
                                        return;
                                    }
                                    const nextVisibleIds = visibleSelectedIds.includes(itemId)
                                        ? visibleSelectedIds.filter((id) => id !== itemId)
                                        : [...visibleSelectedIds, itemId];
                                    syncVisibleSelection(nextVisibleIds);
                                }}
                                onSelectedItemsChange={(_event, selectedIds) => {
                                    const nextVisibleIds = Array.isArray(selectedIds)
                                        ? [...selectedIds]
                                        : selectedIds
                                            ? [selectedIds]
                                            : [];
                                    syncVisibleSelection(nextVisibleIds);
                                }}
                                sx={{
                                    "--TreeView-itemChildrenIndentation": "30px !important",
                                    minWidth: 0,
                                    [`& .${treeItemClasses.content}`]: {
                                        height: "38px",
                                        minWidth: 0,
                                        overflow: "hidden",
                                    },
                                    [`& .${treeItemClasses.label}`]: {
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        minWidth: 0,
                                    },
                                    [`& .MuiTreeItem-content[data-selected]`]: {
                                        borderColor: "transparent",
                                        bgcolor: "transparent",
                                    },
                                    [`& .css-2ji47n-MuiTreeItem-content[data-selected]:hover`]: {
                                        backgroundColor: '#EDEDF4'
                                    },
                                    [`& .MuiSvgIcon-root`]: {
                                        width: '20px',
                                        height: '20px'
                                    },
                                }}
                            />
                        </InfiniteScroll>
                    )}
                </Box>
            </Box>
        );
    },
);
